/**
 * Toast component - individual toast notification
 */

import { useEffect, useState } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import type { Toast as ToastType } from './props/Toast.types';

export interface ToastProps {
  toast: ToastType;
  onRemove: (id: string) => void;
}

const variantStyles = {
  success: {
    container: 'bg-green-50 border-green-200',
    icon: 'text-green-500',
    title: 'text-green-900',
    message: 'text-green-700',
    button: 'text-green-600 hover:text-green-700',
  },
  error: {
    container: 'bg-red-50 border-red-200',
    icon: 'text-red-500',
    title: 'text-red-900',
    message: 'text-red-700',
    button: 'text-red-600 hover:text-red-700',
  },
  warning: {
    container: 'bg-yellow-50 border-yellow-200',
    icon: 'text-yellow-500',
    title: 'text-yellow-900',
    message: 'text-yellow-700',
    button: 'text-yellow-600 hover:text-yellow-700',
  },
  info: {
    container: 'bg-blue-50 border-blue-200',
    icon: 'text-blue-500',
    title: 'text-blue-900',
    message: 'text-blue-700',
    button: 'text-blue-600 hover:text-blue-700',
  },
};

const variantIcons = {
  success: CheckCircleIcon,
  error: XCircleIcon,
  warning: ExclamationTriangleIcon,
  info: InformationCircleIcon,
};

export function Toast({ toast, onRemove }: ToastProps) {
  const [isExiting, setIsExiting] = useState(false);
  const styles = variantStyles[toast.variant];
  const Icon = variantIcons[toast.variant];

  useEffect(() => {
    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, toast.duration);

      return () => clearTimeout(timer);
    }
  }, [toast.duration]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onRemove(toast.id);
    }, 300); // Match animation duration
  };

  return (
    <div
      className={`
        flex items-start gap-3 p-4 rounded-lg border shadow-lg
        transition-all duration-300 ease-in-out
        ${styles.container}
        ${
          isExiting
            ? 'opacity-0 translate-x-full'
            : 'opacity-100 translate-x-0 animate-slide-in-right'
        }
      `}
      role="alert"
      aria-live="polite"
    >
      {/* Icon */}
      <Icon className={`h-6 w-6 shrink-0 ${styles.icon}`} />

      {/* Content */}
      <div className="flex-1 min-w-0">
        {toast.title && (
          <div className={`font-semibold text-sm ${styles.title}`}>{toast.title}</div>
        )}
        <div className={`text-sm ${toast.title ? 'mt-1' : ''} ${styles.message}`}>
          {toast.message}
        </div>
        {toast.action && (
          <button
            onClick={() => {
              toast.action!.onClick();
              handleClose();
            }}
            className={`mt-2 text-sm font-medium underline ${styles.button}`}
          >
            {toast.action.label}
          </button>
        )}
      </div>

      {/* Close button */}
      <button
        onClick={handleClose}
        className={`shrink-0 p-1 rounded-md hover:bg-white/50 transition-colors ${styles.button}`}
        aria-label="Close notification"
      >
        <XMarkIcon className="h-5 w-5" />
      </button>
    </div>
  );
}
